import React, { useMemo, useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useBalances } from '../../wallet/useBalances';
import { useWalletReady } from '../../wallet/useWalletReady';
import { formatCurrency, shortKey } from '../../utils/format';
import { motion, AnimatePresence } from 'framer-motion';
import { isIOSSafari } from '../../hooks/useMobile';
import {
  WalletIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

// Connected indicator pulse
const ConnectedPulse: React.FC = () => (
  <span className="relative flex h-2 w-2">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-consumable opacity-75" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-consumable" />
  </span>
);

export const WalletControls: React.FC = () => {
  const { publicKey, connected, disconnect, wallet, connecting } = useWallet();
  const { setVisible } = useWalletModal();
  const { data: balances } = useBalances(publicKey?.toBase58());
  const { isReady } = useWalletReady();
  const [isPending, setIsPending] = useState(false);

  const balanceDisplay = useMemo(() => {
    if (!balances) return '—';
    return `${formatCurrency(balances.sol, 'SOL')} | ${formatCurrency(balances.usdc, 'USDC')}`;
  }, [balances]);

  // Auto-open modal when ready after pending click
  useEffect(() => {
    if (isPending && isReady) {
      setIsPending(false);
      setVisible(true);
    }
  }, [isPending, isReady, setVisible]);

  const handleClick = () => {
    if (connected) {
      disconnect();
    } else if (isReady) {
      setVisible(true);
    } else {
      // Wallets not ready yet, wait for detection
      setIsPending(true);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1.5">
      <motion.button
        onClick={handleClick}
        className="btn-press group relative overflow-hidden rounded-xl border border-arcade-cyan/30 bg-gradient-to-r from-arcade-cyan/10 via-arcade-purple/10 to-joker/10 px-2 py-2 sm:px-4 sm:py-2.5 transition-all duration-300 hover:border-arcade-cyan/60 hover:shadow-lg hover:shadow-arcade-cyan/20 min-h-[44px] min-w-[44px]"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Animated gradient background on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-arcade-cyan/0 via-arcade-cyan/10 to-arcade-cyan/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <div className="relative flex items-center gap-2 sm:gap-3">
          {connected ? (
            <div className="flex items-center gap-2 sm:gap-3">
              <ConnectedPulse />
              {/* Hide wallet details on mobile, show icon only */}
              <div className="hidden sm:flex flex-col items-start">
                <span className="text-[10px] uppercase tracking-wider text-white/40">
                  {wallet?.adapter.name || 'Wallet'}
                </span>
                <span className="font-mono text-sm font-medium text-white">
                  {publicKey ? shortKey(publicKey.toBase58()) : '—'}
                </span>
              </div>
              <div className="rounded-lg bg-white/5 p-1.5 transition-colors group-hover:bg-danger/20 sm:ml-2">
                <ArrowRightOnRectangleIcon className="h-4 w-4 text-white/40 transition-colors group-hover:text-danger" />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 sm:gap-2.5">
              <div className="rounded-lg bg-arcade-cyan/20 p-1.5">
                {isPending || connecting ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-arcade-cyan" />
                ) : (
                  <WalletIcon className="h-4 w-4 text-arcade-cyan" />
                )}
              </div>
              {/* Hide text on mobile */}
              <span className="hidden sm:inline font-arcade text-sm text-white">
                {isPending || connecting ? 'Connecting...' : 'Connect Wallet'}
              </span>
              {!isPending && !connecting && (
                <ChevronRightIcon className="hidden sm:block h-4 w-4 text-arcade-cyan/60 transition-transform group-hover:translate-x-0.5" />
              )}
            </div>
          )}
        </div>
      </motion.button>

      {/* Balance display when connected */}
      <AnimatePresence>
        {connected && (
          <motion.span
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="hidden sm:inline font-mono text-xs text-white/50"
          >
            {balanceDisplay}
          </motion.span>
        )}
      </AnimatePresence>

      {/* iOS Safari wallet guidance */}
      <AnimatePresence>
        {!connected && isIOSSafari() && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="text-[10px] sm:text-xs text-amber-300/80 text-right max-w-[180px] sm:max-w-none"
          >
            Open in Phantom or Solflare app to connect
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};
